// ApproachContext.tsx
import React, { createContext, useContext, useMemo, ReactNode, useEffect } from 'react';
import { useGetApproaches } from '../../hooks/api/Approaches/useGetApproaches';
import { useGetTaxSettings } from '../../hooks/api/TaxSettings/useGetTaxSettings';
import { DEFAULT_ANNUAL_TAX_RATE } from '../../utility/taxSettings';
import { useGetFixedCostSettings } from '../../hooks/api/FixedCostSettings/useGetFixedCostSettings';
import { CalculatedApproach } from '../../components/decision-making-page/approach-select/calculatedApproach';
import { usePropertyContext } from './PropertyContext';
import { useAvmContext } from './AvmContext';
import { useDecisionFormContext } from './DecisionFormContext';
import { ComparisonConditions } from '../../components/configurations/approaches/approachTypes';
import { ApproachContextType } from './DMContextInterfaces';

const ApproachContext = createContext<ApproachContextType | undefined>(undefined);

export function ApproachProvider({ children }: { children: ReactNode }) {
  const { propertyDetails, isLoadingPropertyDetails } = usePropertyContext();
  const { soldAvmResult } = useAvmContext();
  const {
    state: { fixedCosts, estimatedValue, approachId },
    dispatch,
  } = useDecisionFormContext();

  const {
    data: approaches,
    isLoading: isLoadingApproaches,
    error: isErrorApproaches,
  } = useGetApproaches();

  const { data: taxSettings, isLoading: isLoadingTaxSettings } = useGetTaxSettings();
  // Wait for the rate so max bid doesn't flash a number that ignores taxes; fall back to the default if it can't load.
  const annualTaxRate = taxSettings?.annual_tax_rate ?? DEFAULT_ANNUAL_TAX_RATE;

  // Same idea for Fixed Costs settings; if they can't load, fixed costs apply everywhere (the system behavior).
  const { data: fixedCostSettings, isLoading: isLoadingFixedCostSettings } = useGetFixedCostSettings();
  const applyFixedCostsToBid = fixedCostSettings?.settings?.apply_to_max_bid ?? true;
  const applyFixedCostsToConditions = fixedCostSettings?.settings?.apply_to_conditions ?? true;

  const calculatedApproaches = useMemo(() => {
    if (!approaches || !propertyDetails || !soldAvmResult || isLoadingTaxSettings || isLoadingFixedCostSettings) return [];
    const condition = new ComparisonConditions(propertyDetails, fixedCosts || 0, soldAvmResult, applyFixedCostsToConditions);
    return condition
      .filterApplicableApproaches(approaches)
      .map(a => new CalculatedApproach(a, propertyDetails, fixedCosts || 0, estimatedValue || 0, annualTaxRate, applyFixedCostsToBid));
  }, [
    approaches, propertyDetails, soldAvmResult, fixedCosts, estimatedValue, annualTaxRate, isLoadingTaxSettings,
    isLoadingFixedCostSettings, applyFixedCostsToBid, applyFixedCostsToConditions,
  ]);

  const selectedApproach = useMemo(() => {
    if (!approachId || !calculatedApproaches.length) return null;
    return calculatedApproaches.find(a => a.approach.id === approachId) ?? null;
  }, [ approachId, calculatedApproaches ]);

  const handleApproachChange = (approach: CalculatedApproach | null) => {
    if (!approach) {
      dispatch({
        type: 'SET_FORM_FIELD',
        payload: [
          { field: 'approachId', value: null },
          { field: 'maxBid', value: null },
          { field: 'maxBidPerSqft', value: null },
          { field: 'estimatedProfit', value: null },
          { field: 'roi', value: null },
        ],
      });
    } else {
      dispatch({
        type: 'SET_FORM_FIELD',
        payload: [
          { field: 'approachId', value: approach.approach.id },
          { field: 'maxBid', value: approach.max_bid },
          { field: 'maxBidPerSqft', value: approach.max_bid_per_sqft },
          { field: 'estimatedProfit', value: approach.estimated_profit },
          { field: 'roi', value: approach.roi },
        ],
      });
    }
  };

  useEffect(() => {
    if (selectedApproach) {
      console.log('something changed in approachContext and we have a selected approach, ', selectedApproach);
      console.log('fixedCosts', fixedCosts);
      console.log('estimatedValue', estimatedValue);
      handleApproachChange(selectedApproach);
    }
  }, [ fixedCosts, estimatedValue, selectedApproach ]);

  return (
    <ApproachContext.Provider
      value={{
        approaches,
        isLoadingApproaches: isLoadingApproaches || isLoadingPropertyDetails,
        isErrorApproaches,
        calculatedApproaches,
        selectedApproach,
        handleApproachChange,
      }}>
      {children}
    </ApproachContext.Provider>
  );
}

export function useApproachContext() {
  const ctx = useContext(ApproachContext);
  if (!ctx) throw new Error('useApproachContext must be used within ApproachProvider');
  return ctx;
}
